import constants from '@Common/Core/Constant';
import { IDestroyable, safeDestroys } from '@Common/Core/Destroyable';
import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import { EnumValue, FilterType, TypeKind } from '@Common/Enums';
import * as commonUtils from '@Common/Helpers/CommonUtils';
import timeHelper from '@Common/Helpers/TimeHelper';
import { FieldCondition } from '@InstanceFactory';
import { CheckBox, DropDown, Input, InputDropDown } from '.';

export interface IFilterFieldParam {
    css?: any;
    className?: string;
    width?: string | string;
    height?: string | string;
    fieldType: TypeKind;
    margin?: number | string;
    label: {
        width?: number | string;
        styleName?: string;
        content: string;
    };
    operator: {
        width?: number | string;
        show?: boolean;
        items: EnumValue<FilterType>[];
    };
    input: {
        width?: number | string;
        canEdit?: boolean;
        showTime?: boolean;
        format?: string;
        rowsCount?: number;
    };
}

export class FilterField implements IDestroyable {
    private from: Input | CheckBox | InputDropDown;
    private to: Input;
    private readonly operatorsDropDown: DropDown;
    private readonly root: JQuery;
    private readonly label: JQuery;
    private readonly operatorCell: JQuery;
    private readonly valueCell: JQuery;

    private fieldPath: string | undefined;
    private currentOperatorValue: string | undefined;
    private readonly valuesSeparator = ';';

    private readonly eventBag = new MulticastDelegateBag();
    public readonly onEnterPressed = this.eventBag.create<ICallback<JQuery.Event>>();
    public readonly onDataRequested = this.eventBag.create<(startResultIndex: number, pageSize: number) => Promise<any>>();
    public readonly onChange = this.eventBag.create<(field: FieldCondition) => any>();

    public destroy(): void {
        safeDestroys(this.from, this.to, this.operatorsDropDown, this.root, this.label, this.operatorCell, this.valueCell, this.eventBag);
    }

    constructor(private readonly param: IFilterFieldParam) {
        this.param = param = _.defaults(param, {
            css: null,
            className: '',
            width: 'auto',
            height: 'auto',

            fieldType: TypeKind.string,
            margin: 0,
            label: {
                width: 80,
                styleName: '',
                content: strings.filterField
            },
            operator: {
                width: 100,
                show: true,
                items: []
            },
            input: {
                width: 100,
                canEdit: true,
                showTime: false,
                format: constants.defaultDateTimeFormat,
                rowsCount: 1
            }
        });

        this.root = $('<div/>')
            .addClass('filter-field')
            .toggleClass('multiline', this.param.input.rowsCount > 1)
            .css(
                'width',
                _.toNumber(param.label.width) + (param.operator.show ? _.toNumber(param.operator.width) + 5 : 0) + _.toNumber(param.input.width) + 5
            );

        this.label = $('<label/>')
            .addClass(`${param.label.styleName} text-no-wrap`)
            .css('width', param.label.width)
            .attr('title', param.label.content)
            .text(param.label.content)
            .appendTo(this.root);

        this.operatorCell = $('<div/>').addClass('operator-cell').appendTo(this.root);
        this.valueCell = $('<div/>').addClass('value-cell').appendTo(this.root);

        if (param.fieldType === TypeKind.bool) {
            this.valueCell.css({ textAlign: 'center' });
        }

        if (param.operator.show) {
            this.operatorsDropDown = new DropDown({
                width: param.operator.width /*blockElement: false,*/,
                sortable: false
            });
            this.operatorsDropDown.setItems(param.operator.items);
            this.operatorsDropDown
                .getControl()
                .css({
                    display: 'inline-block',
                    verticalAlign: 'bottom' /*, marginRight:"10px"*/
                })
                .appendTo(this.operatorCell);
            this.operatorsDropDown.onChange(() => {
                const data = this.getValue(true);
                this.setValue(data, true);
                this.onChange(data);
            });
        }

        this.createControls(param.fieldType);

        if (this.operatorsDropDown) {
            this.currentOperatorValue = this.operatorsDropDown.getFirstOrDefaultId();
        }
    }

    public toStr(): string {
        const filter = this.getValue();
        // удаляем фильтры с оператором типа unknown, т.к. в ConditionControl.getValue они тоже удаляются
        if (filter.operatorType !== FilterType.unknown && filter.values.length) {
            return `${this.param.label.content} ${FilterType.getValue(filter.operatorType).name} ${filter.values.join(',')}`;
        }
        return '';
    }

    public getValue(auto?: boolean): FieldCondition {
        let operatorType = null;
        if (this.param.operator.show) {
            operatorType = this.currentOperatorValue == null ? FilterType.unknown : this.currentOperatorValue;
        }

        const fromVal = this.fromValue();

        let values = [];
        if (operatorType !== FilterType.isNull && operatorType !== FilterType.isNotNull) {
            if (!_.isNil(fromVal)) {
                if (this.isWhereIn(operatorType)) {
                    values = this.valueToArray(fromVal);
                } else {
                    values.push(fromVal);
                    if (this.to && (operatorType === FilterType.range || operatorType === FilterType.between)) {
                        values.push(this.to.getValue());
                    }
                }
            }
        }

        const condition = FieldCondition.create();
        condition.fieldPath = this.fieldPath;
        condition.operatorType =
            this.operatorsDropDown && this.operatorsDropDown.getFirstOrDefaultId() != null
                ? this.operatorsDropDown.getFirstOrDefaultId()
                : FilterType.unknown;
        condition.values = values;
        let filterCondition = condition;

        if (!auto && this.param.fieldType === TypeKind.dateTime && !this.param.input.showTime) {
            filterCondition = commonUtils.clone(filterCondition);

            if (filterCondition.operatorType === FilterType.greater && filterCondition.values[0] != null) {
                filterCondition.values[0].setDate(filterCondition.values[0].getDate() + 1);
                filterCondition.operatorType = FilterType.greaterOrEqual;
            }

            if (filterCondition.operatorType === FilterType.lessOrEqual && filterCondition.values[0] != null) {
                filterCondition.values[0].setDate(filterCondition.values[0].getDate() + 1);
                filterCondition.operatorType = FilterType.less;
            }

            if (filterCondition.operatorType === FilterType.range && filterCondition.values[0] != null && filterCondition.values[1] != null) {
                filterCondition.values[1].setDate(filterCondition.values[1].getDate() + 1);
            }

            if (filterCondition.operatorType === FilterType.equal && filterCondition.values[0] != null) {
                filterCondition.values[1] = new Date(filterCondition.values[0].getTime());
                filterCondition.values[1].setDate(filterCondition.values[1].getDate() + 1);
                filterCondition.operatorType = FilterType.range;
            }
        }
        return filterCondition;
    }

    // eslint-disable-next-line complexity
    public setValue(filterCondition: FieldCondition, auto = false): void {
        if (!auto && this.param.fieldType === TypeKind.dateTime && !this.param.input.showTime) {
            filterCondition = commonUtils.clone(filterCondition);

            if (
                filterCondition.operatorType === FilterType.range &&
                filterCondition.values[0] != null &&
                filterCondition.values[0] !== '' &&
                filterCondition.values[1] != null &&
                filterCondition.values[1] !== ''
            ) {
                if (_.isString(filterCondition.values[0])) {
                    filterCondition.values[0] = timeHelper.dateTimeFromString(filterCondition.values[0]);
                }
                if (_.isString(filterCondition.values[1])) {
                    filterCondition.values[1] = timeHelper.dateTimeFromString(filterCondition.values[1]);
                }

                if (_.isDate(filterCondition.values[0]) && _.isDate(filterCondition.values[1])) {
                    filterCondition.values[1].setDate(filterCondition.values[1].getDate() - 1);
                    if (filterCondition.values[0].valueOf() === filterCondition.values[1].valueOf()) {
                        filterCondition.operatorType = FilterType.equal;
                        filterCondition.values.length = 1;
                    }
                }
            }
        }

        this.fieldPath = filterCondition.fieldPath;

        if (this.from) {
            this.from.destroy();
            this.from = null;
        }
        if (this.to) {
            this.to.destroy();
            this.to = null;
        }

        this.createControls(filterCondition.operatorType);

        if (this.param.operator.show) {
            if (FilterType.is(filterCondition.operatorType)) {
                this.operatorsDropDown.selectedItems([{ id: filterCondition.operatorType }]);
            } else {
                this.operatorsDropDown.selectedItems([this.param.operator.items[0]]);
            }

            if (this.from && this.operatorsDropDown.getFirstOrDefaultId() === FilterType.unknown) {
                this.fromState(false);
            }
            if (this.to && this.operatorsDropDown.getFirstOrDefaultId() === FilterType.unknown) {
                this.to.setState(false);
            }
            this.currentOperatorValue = this.operatorsDropDown.getFirstOrDefaultId();
        }

        let value;
        if (this.from) {
            if (filterCondition.values[0] != null) {
                if (this.isWhereIn(filterCondition.operatorType)) {
                    const stringValue = filterCondition.values.join(this.valuesSeparator + ' ');
                    if (this.usingDropDown(filterCondition.operatorType)) {
                        (this.from as InputDropDown).setTitleDescription(stringValue);
                    } else {
                        (this.from as Input).setValue(stringValue);
                    }
                } else {
                    value = filterCondition.values[0];
                    if (this.param.fieldType === TypeKind.bool) {
                        (this.from as CheckBox).value(value === '' ? null : !!value);
                    } else {
                        if (this.param.fieldType === TypeKind.dateTime && value && _.isString(value)) {
                            value = timeHelper.dateTimeFromString(value);
                        }
                        (this.from as Input).setValue(value);
                    }
                }
            } else {
                if (!this.isDropDown(this.from) && !this.isInput(this.from)) {
                    this.from.value(null);
                }
            }
        }

        if (this.to) {
            if (filterCondition.values[1] != null) {
                value = filterCondition.values[1];
                if (this.param.fieldType === TypeKind.dateTime && value && _.isString(value)) {
                    value = timeHelper.dateTimeFromString(value);
                }
                this.to.setValue(value);
            }
        }
    }

    public getControl(): JQuery {
        return this.root;
    }

    public getLabelCell(): JQuery {
        return this.label;
    }

    public getOperatorCell(): JQuery {
        return this.operatorCell;
    }

    public getValueCell(): JQuery {
        return this.valueCell;
    }

    public clear(): void {
        const currentValue = this.getValue();
        currentValue.values = [];
        this.setValue(currentValue);
    }

    private createControls(filterType): void {
        const format =
            this.param.fieldType === TypeKind.dateTime && this.param.input.canEdit
                ? this.param.input.showTime
                    ? constants.invariantCultureDateTimeFormat
                    : constants.invariantCultureDateFormat
                : this.param.input.format;

        if (filterType === FilterType.isNull || filterType === FilterType.isNotNull) {
            return;
        }

        if ((filterType === FilterType.between || filterType === FilterType.range) && this.param.input.canEdit) {
            const inputWidth = 'calc(50% - 5px)';
            const fromInput = new Input({
                type: this.param.fieldType,
                width: inputWidth,
                canEdit: this.param.input.canEdit,
                showTime: this.param.input.showTime
                // format: format
            });
            fromInput.onChange(() => {
                const value = this.getValue();
                this.onChange(value);
            });
            fromInput.onEnterPressed(e => {
                this.onEnterPressed(e);
            });
            this.from = fromInput;
            this.from.getControl().css('margin-right', '10px').addClass('from').appendTo(this.valueCell);

            const toInput = new Input({
                type: this.param.fieldType,
                width: inputWidth,
                canEdit: this.param.input.canEdit,
                showTime: this.param.input.showTime
                // format: format
            });

            toInput.onChange(() => {
                const value = this.getValue();
                this.onChange(value);
            });
            toInput.onEnterPressed(e => {
                this.onEnterPressed(e);
            });
            this.to = toInput;
            this.to.getControl().addClass('to').appendTo(this.valueCell);
        } else if (this.param.fieldType === TypeKind.bool) {
            const fromCheckBox = new CheckBox({
                width: this.param.input.width,
                inlineBlockElement: true,
                tristate: true
                // margins:{left:"auto", right:"auto"}
            });
            fromCheckBox.onChange(() => {
                const value = this.getValue();
                this.onChange(value);
            });
            this.from = fromCheckBox;
            this.from.getControl().css({ verticalAlign: 'bottom' }).appendTo(this.valueCell);
        } else if (this.param.fieldType !== TypeKind.binary) {
            if (this.usingDropDown(filterType)) {
                let startIndex = 0;
                let noMoreData = false;
                const pageSize = 100;

                const loadData = async () => {
                    if (!noMoreData) {
                        const control = this.from.getControl();
                        control.spinnerStart({ loadingTimeout: 0 });

                        const data = await this.onDataRequested(startIndex, pageSize);
                        noMoreData = data.length < pageSize;
                        list.appendItems(data);
                        control.spinnerStop();

                        startIndex += pageSize;
                        fromDropDown.showContent();
                    }
                };

                const fromDropDown = new InputDropDown({
                    width: this.param.input.width,
                    height: 30,

                    checkable: true,
                    checkOnClick: true,

                    lazyLoad: true,
                    skipKeyHandling: true,

                    searchPanel: true,
                    showClearAllButton: true,
                    hideEmpty: false,

                    onSearch: data => fromDropDown.setCompareItems(data.values[0]),
                    onAppendData: async () => await loadData()
                });
                const list = fromDropDown.getList();
                fromDropDown.onShowContent(async () => {
                    if (startIndex == 0) {
                        await loadData();
                    }

                    const items = this.valueToArray(fromDropDown.getInput().val()).map(id => ({ id, value: id }));
                    list.checkedItems(items);
                    updateTitle();
                });
                fromDropDown.getInput().on('keyup', e => {
                    if (e.which === constants.keyCodes.ENTER) {
                        this.onEnterPressed(e);
                    }
                });
                list.onItemCheck((item, param) => {
                    if (param?.clientCheck) {
                        updateTitle();
                    }
                });
                list.onSelectAllChanged(isAll => {
                    updateTitle();
                });
                const updateTitle = () =>
                    fromDropDown.setTitleDescription(
                        list
                            .checkedItems()
                            .map(i => i.name)
                            .join(`${this.valuesSeparator} `)
                    );
                this.from = fromDropDown;
                this.from.getControl().appendTo(this.valueCell);
            } else {
                let inputType = this.param.fieldType;
                if (this.param.fieldType === TypeKind.text || this.param.fieldType === TypeKind.guid) {
                    inputType = this.param.input.rowsCount > 1 ? TypeKind.text : TypeKind.string;
                }
                const fromInput = new Input({
                    type: inputType,
                    width: this.param.input.width,
                    canEdit: this.param.input.canEdit,
                    showTime: this.param.input.showTime,
                    // format: format,
                    rowsCount: this.param.input.rowsCount,
                    height: this.param.input.rowsCount > 1 ? '' : undefined
                });
                fromInput.onChange(() => {
                    const value = this.getValue();
                    this.onChange(value);
                });
                fromInput.onEnterPressed(e => {
                    this.onEnterPressed(e);
                });
                this.from = fromInput;
                this.from.getControl().appendTo(this.valueCell);
            }
        }
    }

    private fromValue(): any {
        const ctrl = this.from;
        if (ctrl) {
            if (this.isDropDown(ctrl)) {
                return ctrl.getInput().val();
            } else if (this.isInput(ctrl)) {
                return ctrl.getValue();
            } else {
                return ctrl.value();
            }
        }
        return '';
    }

    private fromState(state: boolean): void {
        if (this.from) {
            this.from.enabled ? this.from.enabled(state) : this.from.setState(state);
        }
    }

    private isInput(ctrl: Input | CheckBox | InputDropDown): ctrl is Input {
        return !!(ctrl as Input).getValue;
    }

    private isDropDown(ctrl: Input | CheckBox | InputDropDown): ctrl is InputDropDown {
        return !!(ctrl as InputDropDown).getList;
    }

    private isWhereIn(operatorType: FilterType): boolean {
        return operatorType === FilterType.whereIn || operatorType === FilterType.whereNotIn;
    }

    private usingDropDown(operatorType: FilterType): boolean {
        return false; // this.isWhereIn(operatorType) && this.param.fieldType === TypeKind.string && this.param.input.canEdit;
    }

    private valueToArray(str): string[] {
        return _.chain(str).split(this.valuesSeparator).map(_.unary(_.trim)).compact().value();
    }
}
